import { SalesReportTransactionResponse } from '../../types';
import { ReportsEmptyState } from '../empty-state';
import { format } from 'date-fns';
import { formatAmount } from '../../../../../utils/formatAmount';
import ThermalUsbPrinter from '../../../../../lib/printer';
import { useMutation } from 'react-query';
import { getSessionInfo } from '../../../../../utils/getSessionInfo';

const SalesDetailsNew = ({ salesData }: { salesData: SalesReportTransactionResponse | undefined }) => {
	const thermalPrinter = new ThermalUsbPrinter();

	const { userInfo } = getSessionInfo() ?? {};

	const { mutateAsync: printReceipt, isLoading } = useMutation({
		mutationFn: async () => {
			if (!salesData) return;
			await thermalPrinter.printReceipt({
				data: { ...salesData, transactionId: salesData?.invoiceId as unknown as number } as any,
				userInfo,
				totalSumOfAmountPaid: String(salesData.amountPaid),
				totalSavings: String(salesData?.payableAmount - salesData?.amountPaid),
				cartSumWithoutDiscount: String(salesData.totalAmount),
				vatAmount: String(salesData.taxTotal),
			});
		},
	});

	if (!salesData)
		return (
			<div className="h-auto md:h-[90vh] min-h-[50vh]">
				<h3 className="text-[1rem] text-[#4A5568] text-center">Transaction Detail</h3>

				<div className="flex items-center self-center justify-center h-[50vh] md:h-[60vh]">
					<ReportsEmptyState />
				</div>
			</div>
		);

	const salesitemLength = salesData?.products?.length;

	return (
		<div className="max-h-[50vh] md:max-h-[65vh] overflow-auto custom-scrollbar px-3">
			<h3 className="text-[1rem] text-[#4A5568] text-center">Transaction Detail</h3>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0 mt-4">
				<div>
					<h4 className="text-[0.875rem] text-[#4A5568]">Date</h4>
				</div>

				<div className="text-[0.875rem] text-right sm:text-right">
					{format(new Date(salesData.createdAt), 'dd LLL, yyyy h:mm a')}
				</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<div>
					<h4 className="text-[0.875rem] text-[#4A5568]">Invoice</h4>
				</div>

				<div className="text-[0.875rem] text-right sm:text-right">{salesData?.invoiceId}</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<div>
					<h4 className="text-[0.875rem] text-[#4A5568]">Customer Name</h4>
				</div>

				<div className="text-[0.875rem] text-[#A63494] text-right sm:text-right">{salesData?.customer?.name ?? 'N/A'}</div>
			</div>

			<div className="h-[1px] bg-[#CBD5E0] my-4"></div>

			<div>
				{salesData.products.map((product) => {
					return (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0 mb-3">
							<div className="font-[600] text-[0.875rem]">{product?.productName}</div>

							<div className="font-[700] text-[0.875rem] text-right sm:text-right">
								{formatAmount(product?.totalAmount)}
							</div>
						</div>
					);
				})}
			</div>

			<div className="h-[1px] bg-[#CBD5E0] my-4"></div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<h4 className="font-[600] text-[0.875rem]">Subtotal</h4>

				<div className="font-[700] text-[0.875rem] text-right sm:text-right">{formatAmount(salesData?.totalAmount)}</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<h4 className="font-[600] text-[0.875rem]">VAT ({salesData?.taxPercentage ?? 0}%)</h4>

				<div className="font-[700] text-[0.875rem] text-right sm:text-right">{formatAmount(salesData?.taxTotal)}</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<h4 className="font-[600] text-[0.875rem]">
					<span className="text-[#A63494]">Total </span>
					<span className="text-[#4A5568]">
						({salesitemLength} {salesitemLength > 1 ? 'Items' : 'Item'})
					</span>
				</h4>

				<div className="font-[700] text-[0.875rem] text-[#A63494] text-right sm:text-right">
					{formatAmount(salesData?.payableAmount)}
				</div>
			</div>

			<div className="h-[1px] bg-[#CBD5E0] my-4"></div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<h4 className="font-[600] text-[#4A5568] text-[12px]">Cashier</h4>

				<div className="font-[700] text-[12px] text-right sm:text-right">{salesData?.cashier?.name}</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<h4 className="font-[600] text-[12px] text-[#4A5568]">Payment Method</h4>

				<div className="font-[700] text-[12px] text-right sm:text-right">
					{salesData?.payments?.map((payment) => payment?.method).join(', ')}
				</div>
			</div>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
				<h4 className="font-[600] text-[12px] text-[#4A5568]">Outstanding Amount</h4>

				<div className="font-[700] text-[12px] text-right sm:text-right">
					{formatAmount(salesData?.outstandingAmount) ?? 0}
				</div>
			</div>

			<div className="mt-5 flex justify-center sm:justify-end">
				<button
					disabled={isLoading}
					onClick={() => printReceipt()}
					className="px-4 py-2 bg-[#A63494] text-white rounded-md w-full sm:w-auto"
				>
					{isLoading ? 'Printing...' : 'Print Receipt'}
				</button>
			</div>
		</div>
	);
};

export default SalesDetailsNew;
